import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ResourceLibrary = ({ selectedRole }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [resourceType, setResourceType] = useState('all');
  const [skillLevel, setSkillLevel] = useState('all');

  const resources = [
    {
      id: 1,
      title: 'Introduction to Carbon Project Methodologies',
      type: 'video',
      duration: '45 min',
      level: 'beginner',
      role: 'coordinator',
      description: 'Comprehensive overview of VCS and Gold Standard methodologies',
      views: 1250,
      rating: 4.8
    },
    {
      id: 2,
      title: 'Advanced MRV Techniques for AFOLU Projects',
      type: 'documentation',
      duration: '2 hours',
      level: 'advanced',
      role: 'validator',
      description: 'In-depth technical guide for monitoring and verification',
      views: 890,
      rating: 4.9
    },
    {
      id: 3,
      title: 'GIS Analysis for Carbon Stock Assessment',
      type: 'interactive',
      duration: '3 hours',
      level: 'intermediate',
      role: 'analyst',
      description: 'Hands-on simulation using QGIS and remote sensing data',
      views: 2100,
      rating: 4.7
    },
    {
      id: 4,
      title: 'Article 6 Compliance Certification',
      type: 'certification',
      duration: '8 weeks',
      level: 'advanced',
      role: 'auditor',
      description: 'Official certification program for international carbon markets',
      views: 450,
      rating: 5.0
    },
    {
      id: 5,
      title: 'PDD Writing Workshop',
      type: 'video',
      duration: '1.5 hours',
      level: 'intermediate',
      role: 'specialist',
      description: 'Step-by-step guide to drafting Project Design Documents',
      views: 1680,
      rating: 4.6
    },
    {
      id: 6,
      title: 'Stakeholder Engagement Best Practices',
      type: 'documentation',
      duration: '30 min',
      level: 'beginner',
      role: 'coordinator',
      description: 'Effective communication strategies for carbon projects',
      views: 980,
      rating: 4.5
    }
  ];

  const filteredResources = resources?.filter(resource => {
    const matchesSearch = resource?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         resource?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesType = resourceType === 'all' || resource?.type === resourceType;
    const matchesLevel = skillLevel === 'all' || resource?.level === skillLevel;
    const matchesRole = selectedRole === 'all' || resource?.role === selectedRole;
    return matchesSearch && matchesType && matchesLevel && matchesRole;
  });

  const getResourceIcon = (type) => {
    switch (type) {
      case 'video': return 'Video';
      case 'documentation': return 'FileText';
      case 'certification': return 'Award';
      case 'interactive': return 'Gamepad2';
      default: return 'File';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-green-500/10 text-green-600';
      case 'intermediate': return 'bg-yellow-500/10 text-yellow-600';
      case 'advanced': return 'bg-red-500/10 text-red-600';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              leftIcon="Search"
            />
          </div>
          <Select
            value={resourceType}
            onChange={(e) => setResourceType(e?.target?.value)}
          >
            <option value="all">All Types</option>
            <option value="video">Video Tutorials</option>
            <option value="documentation">Documentation</option>
            <option value="certification">Certifications</option>
            <option value="interactive">Interactive Simulations</option>
          </Select>
          <Select
            value={skillLevel}
            onChange={(e) => setSkillLevel(e?.target?.value)}
          >
            <option value="all">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </Select>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          Training Resources
        </h2>
        <span className="text-sm text-muted-foreground">
          {filteredResources?.length} resource{filteredResources?.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* Resource Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredResources?.map(resource => (
          <div
            key={resource?.id}
            className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={getResourceIcon(resource?.type)} size={24} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-foreground mb-1 truncate">
                  {resource?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {resource?.description}
                </p>
                <div className="flex items-center gap-3 mb-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelColor(resource?.level)}`}>
                    {resource?.level?.charAt(0)?.toUpperCase() + resource?.level?.slice(1)}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>{resource?.duration}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Eye" size={14} />
                    <span>{resource?.views}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Icon name="Star" size={14} className="text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-medium text-foreground">{resource?.rating}</span>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-1.5 hover:bg-muted rounded transition-colors">
                      <Icon name="Bookmark" size={16} className="text-muted-foreground" />
                    </button>
                    <button className="p-1.5 hover:bg-muted rounded transition-colors">
                      <Icon name="Share2" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredResources?.length === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No resources found matching your criteria</p>
        </div>
      )}
    </div>
  );
};

export default ResourceLibrary;